import { Content, DynamicContent, PageSize, Table, TableCell, TDocumentDefinitions } from "pdfmake/interfaces";
import { Moment } from "moment-timezone";
import { Ausgabe, Einnahme, Kassenbuch, Transaktion } from "@/api/Abrechnung";
import { renderPdfToStream, Document } from "zna-lib";
import { MONATSNAMEN } from "../Termine";
import { kassenbuchStyles, noBorder, tableLayouts } from "./pdfStyles";

function format(betrag: number, printZeros = false): string {
  return betrag > 0 || printZeros
    ? betrag.toLocaleString("de-DE", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : "";
}

function kassenbuchPdfHeader(monat: Moment, kassenbuch: Kassenbuch): DynamicContent {
  return function(currentPage: number, pageCount: number, pageSize: PageSize) {
    return [
      {
        layout: "solidHorizontalLines",
        margin: [10, 20, 10, -40],
        table: {
          widths: [100, "*", 100],
          headerRows: 0,
          keepWithHeaderRows: 0,
          body: [
            [
              {
                text: '',
                border: noBorder,
              },
              {
                alignment: 'center',
                text: `Monat ${monat.format('MM/YYYY')}`,
                margin: [0, 0, 0, 5],
                border: noBorder,
              },
              {
                text: "Blatt Nr. " + kassenbuch.blatt,
                alignment: "right",
                margin: [0, 0, 0, 5],
                border: noBorder,
              },
            ],
          ],
        },
      },
    ];
  };
}

interface TabellenZeile {
  einnahme: number;
  ausgabe: number;
  zeile: TableCell[];
}

function createKassenbuchTransaktionPdfZeilen(tx: Transaktion): TabellenZeile[] {
  const einnahme = tx as Einnahme;
  const ausgabe = tx as Ausgabe;
  switch (tx.typ) {
    case "einnahme":
      return [
        {
          einnahme: einnahme.einnahme,
          ausgabe: 0,
          zeile: [
            { alignment: 'center', text: ''+einnahme.tag },
            { alignment: 'center', text: 'Einnahme' },
            { alignment: 'center', text: einnahme.studio },
            { text: '' },
            { alignment: 'center', text: format(einnahme.einnahme) },
            { text: '' },
            { text: '' },
            { text: '' },
          ],
        }, {
          einnahme: einnahme.steuer,
          ausgabe: 0,
          zeile: [
            { alignment: 'center', text: ''+einnahme.tag },
            { alignment: 'center', text: 'Steuer' },
            { alignment: 'center', text: einnahme.studio },
            { text: '' },
            { alignment: 'center', text: format(einnahme.steuer) },
            { text: '' },
            { text: '' },
            { alignment: 'center', text: 'ohne MWSt.' },
          ],
        },
      ];
    case "ausgabe":
      return [{
        einnahme: 0,
        ausgabe: ausgabe.betrag,
        zeile: [
          { alignment: 'center', text: '' + ausgabe.tag },
          { alignment: 'center', text: ausgabe.bezeichnung },
          { text: '' },
          { alignment: 'center', text: ausgabe.beleg ? '' + ausgabe.beleg : '' },
          { text: '' },
          { alignment: 'center', text: format(ausgabe.betrag) },
          { text: '' },
          { text: '' },
        ],
      }];
    default: return [];
  }
}

function createTransaktionenZeilen(saldoAlt: number, transaktionen: Transaktion[]): TableCell[][] {
  function createTransaktionenHeaderZeile(): TableCell[] {
    return [
      { alignment: 'center', style: 'tableTextBold', text: 'Datum' },
      { alignment: 'center', style: 'tableTextBold', text: 'Text' },
      { alignment: 'center', style: 'tableTextBold', text: 'Studio' },
      { alignment: 'center', style: 'tableTextBold', text: 'Beleg Nr.' },
      { alignment: 'center', style: 'tableTextBold', text: 'Eingang' },
      { alignment: 'center', style: 'tableTextBold', text: 'Ausgang' },
      { text: '' },
      { text: '' },
    ];
  }
  function createSummenzeile(summeEinnahmen: number, summeAusgaben: number): TableCell[] {
    return [
      { style: 'tableTextBold', colSpan: 4, alignment: 'center', text: 'Summe:' },{ text: '' },{ text: '' },{ text: '' },
      { style: 'tableTextBold', alignment: 'center', text: format(summeEinnahmen, true) },
      { style: 'tableTextBold', alignment: 'center', text: format(summeAusgaben, true) },
      { text: '' }, { text: '' },
    ];
  }
  function createTransaktionenFooterZeilen(saldoAlt: number, summeEinnahmen: number, summeAusgaben: number) {
    return [
      [
        { style: 'tableTextBold', colSpan: 4, alignment: 'center', text: 'Bestand:' },{ text: '' },{ text: '' },{ text: '' },
        { style: 'tableTextBold', alignment: 'center', text: format(saldoAlt) },
        { style: 'tableTextBold', alignment: 'center', text: format(saldoAlt + summeEinnahmen - summeAusgaben) },
        { text: '' }, { text: '' },
      ],
      [
        { style: 'tableTextBold', colSpan: 4, alignment: 'center', text: 'Summe - Kontrolle:' },{ text: '' },{ text: '' },{ text: '' },
        { style: 'tableTextBold', alignment: 'center', text: format(saldoAlt + summeEinnahmen) },
        { style: 'tableTextBold', alignment: 'center', text: format(saldoAlt + summeEinnahmen) },
        { text: '' }, { text: '' },
      ],
    ] as TableCell[][];
  }

  const ZEILEN_PRO_SEITE = 44;
  const transaktionenZeilen = transaktionen.map(createKassenbuchTransaktionPdfZeilen).flatMap(zeilen => zeilen);
  const ergebnisTabelle: TableCell[][] = [];
  let summeEinnahmen = 0, summeAusgaben = 0, anzahl = 0;
  for (let i = 0; i < transaktionenZeilen.length; i++) {
    if (anzahl == 0) {
      ergebnisTabelle.push(createTransaktionenHeaderZeile());
      anzahl++;
    }
    const zeile = transaktionenZeilen[i];
    summeEinnahmen += zeile.einnahme;
    summeAusgaben += zeile.ausgabe;
    ergebnisTabelle.push(zeile.zeile);
    anzahl++;
    if (anzahl + 3 > ZEILEN_PRO_SEITE) {
      ergebnisTabelle.push(createSummenzeile(summeEinnahmen, summeAusgaben));
      ergebnisTabelle.push(...createTransaktionenFooterZeilen(saldoAlt, summeEinnahmen, summeAusgaben));
      saldoAlt += summeEinnahmen - summeAusgaben;
      anzahl = summeEinnahmen = summeAusgaben = 0;
    } else if (i == transaktionenZeilen.length - 1) {
      for (let leerzeile = anzahl; leerzeile < ZEILEN_PRO_SEITE-3; leerzeile++) {
        ergebnisTabelle.push([{text:' '},{text:' '},{text:' '},{text:' '},{text:' '},{text:' '},{text:' '},{text:' '},]);
      }
      ergebnisTabelle.push(createSummenzeile(summeEinnahmen, summeAusgaben));
      ergebnisTabelle.push(...createTransaktionenFooterZeilen(saldoAlt, summeEinnahmen, summeAusgaben));
    }
  }
  return ergebnisTabelle;
}

export function createKassenbuchDruckansicht(
  monat: Moment,
  transaktionen: Transaktion[],
  kassenbuch: Kassenbuch,
  saldoVormonat: number,
): Promise<Document> {
  const monatsname = MONATSNAMEN[monat.month()];
  const docDefinition: TDocumentDefinitions = {
    info: {
      title: `Kassenbuch ${monatsname}/${monat.year()}`,
      author: 'ZNA GmbH',
      subject: 'Kassenbuch',
      keywords: `Kassenbuch, ZNA, ZNA GmbH, ${monatsname} ${monat.year()}`,
    },
    header: kassenbuchPdfHeader(monat, kassenbuch),
    content: [
      //kassenbuchTable,
      {
        style: "tableExample",
        color: "#444",
        margin: [0, 0, 0, 0],
        table: {
          widths: [35, '*', 50, 50, 50, 50, 80, 60],
          body: [
            ...createTransaktionenZeilen(saldoVormonat, transaktionen),
          ]
        },
      }    
    ],
    styles: kassenbuchStyles,
    defaultStyle: {
      // alignment: 'justify'
      fontSize: 10,
      bold: false,
      margin: [0, 0, 0, 10],
    },
    pageMargins: [10, 70, 10, 10],
    permissions: {
      printing: 'highResolution', //'lowResolution'
      modifying: false,
      copying: true,
      annotating: false,
      fillingForms: true,
      contentAccessibility: false,
      documentAssembly: false,
    },
  };

  return renderPdfToStream(docDefinition, {}, tableLayouts);
}
